import Layout from "../../components/Layout/Layout";
import NewButton from "../../components/NewButton/NewButton";
import Search from "../../components/Search/Search";
import Edit from "../../assets/images/Edit.svg";
import GreenDot from "../../assets/images/GreenDot.svg";
import RedDot from "../../assets/images/RedDot.svg";
import { Fragment, useCallback, useEffect, useState } from "react";
import EditUserModal from "../../components/EditUserModal/EditUserModal";
import AddUserModal from "../../components/AddUserModal/AddUserModal";
import ApiService from "../../services/ApiService";
import SearchableDown from "../../components/SearchableDown/SearchableDown";
import MuiAlert from "../../components/Alert/Alert";
import ViewUserModal from "../../components/ViewUserModal/ViewUserModal";
import { IUser } from "../../helpers/Interfaces";
import FileProgress from "../../components/FileProgress/FileProgress";
import SearchIcon from "../../assets/images/Search.svg";
import { UserFilterTypes } from "../../helpers/Types";
import RemoveFilter from "../../assets/images/RemoveFilter.svg";
import { validDateField, validStringField } from "../../helpers/HelperFunctions";

import "./Users.scss";

export default function Users() {

    const apiService = new ApiService();

    const [loading, setLoading] = useState(false);
    const [userModals, setUserModals] = useState({ add: false, edit: false, view: false });
    const [users, setUsers] = useState([] as IUser[])
    const [selectedUser, setSelectedUser] = useState({} as IUser)
    const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
    const [anchorSearcherEl, setAnchorSearcherEl] = useState<HTMLImageElement>(null as any);
    const [selectedFilter, setSelectedFilter] = useState<UserFilterTypes>("firstName");
    const [showCancelFilter, setShowCancelFilter] = useState([] as any);
    const [filterUsers, setFilterUsers] = useState([] as IUser[]);
    const [showSearch, setShowSearch] = useState(true);

    useEffect(() => {
        getUsers();
    }, [])

    const handleSearcherClick = (event: React.MouseEvent<HTMLImageElement>, filterBy: UserFilterTypes) => {
        setAnchorSearcherEl(event.currentTarget);
        setSelectedFilter(filterBy);
    };

    const handleSelectedUser = (selectedValue: string) => {
        filter(selectedFilter, selectedValue);
        setAnchorSearcherEl(null as any);
        setShowSearch(true);
    }

    const handleClearFilter = () => {
        setFilterUsers(users);
    }

    useEffect(() => {
        if (users.length !== filterUsers.length) {
            setShowCancelFilter(true);
        } else {
            setShowCancelFilter(false);
        }
    }, [filterUsers])

    function filter(filterBy: UserFilterTypes, query: string) {
        let filterList = filterUsers?.filter((user: any) => {
            return user[filterBy] === query;
        })

        setFilterUsers(filterList);
    }

    function getInitialValues() {

        let valueArr = [] as any[];

        users.forEach((report: any) => {
            if (report[selectedFilter] != null) {
                valueArr.push(report[selectedFilter])
            }
        });

        let distinctValues = valueArr.filter((value, index, array) => array.indexOf(value) === index);

        return distinctValues;
    }

    const handleSearcherClose = () => {
        setAnchorSearcherEl(null as any);
    };

    const openSearcher = Boolean(anchorSearcherEl);

    const handleOpen = (modal: "add" | "edit" | "view") => {
        let newUserModals = userModals;
        newUserModals[modal] = true;
        setUserModals((prevDetails) => ({ ...prevDetails, ...newUserModals }));
    }

    const handleClose = useCallback((modal: "add" | "edit" | "view", shouldUpdate = false) => {
        let newUserModals = userModals;
        newUserModals[modal] = false;
        setUserModals((prevDetails) => ({ ...prevDetails, ...newUserModals }));

        if (shouldUpdate) {
            getUsers();
        }
    }, [])

    function getUsers() {

        setLoading(true);

        apiService.getUsers().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            setUsers(data?.users);
            setFilterUsers(data?.users);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function search(searchedWord: string) {

        if (searchedWord.trim() === "") {
            handleClearFilter();
            return;
        }

        let searchArr = [] as IUser[];

        filterUsers.forEach((user) => {
            if (user.firstName.toLowerCase().includes(searchedWord.toLowerCase())) {
                searchArr.push(user);
            }

            if (user.lastName.toLowerCase().includes(searchedWord.toLowerCase())) {
                searchArr.push(user);
            }

            if (user.email.toLowerCase().includes(searchedWord.toLowerCase())) {
                searchArr.push(user);
            }
        })

        let distinctArr = searchArr.filter((value, index) => searchArr.indexOf(value) === index);

        setFilterUsers(distinctArr);
    }

    return (
        <Layout selectedIndex={4}>
            <div className="users-container">
                <MuiAlert error={showAlert.isError} message={showAlert.message} open={showAlert.open}
                    onClose={() => {
                        let newShowAlert = showAlert;
                        newShowAlert.open = false;
                        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert, }));
                    }} />
                <div className="top-bar container-header animated animatedFadeInUp fadeInUp">
                    <span className="text">Users</span>
                    <div className="elements search-box">
                        <Search onChange={search} />
                        <NewButton text="+ Add User" onClick={() => { handleOpen("add") }} />
                    </div>
                </div>
                <div className="table animated animatedFadeInUp fadeInUp">
                    <div className="header">
                        <div className="header-row large">
                            <span className="large-text">Name</span>
                            <img onClick={(value) => { handleSearcherClick(value, "firstName"); }} className="icon"
                                src={SearchIcon} alt={"Search"} />
                        </div>
                        <div className="header-row small">
                            <span className="large-text">Status</span>
                        </div>
                        <div className="header-row large">
                            <span className="large-text">Last Login</span>
                            <span className="filter-content">
                                {showCancelFilter
                                    ? <img className="filter-icon" src={RemoveFilter} alt={"RemoveFilter"} onClick={handleClearFilter} />
                                    : <div className="filter-icon" />
                                }
                            </span>
                        </div>
                    </div>
                    <div className="data animated animatedFadeInUp fadeInUp">
                        {filterUsers?.map((user) => {
                            return (<div key={user.email} className="data-row">
                                <div className="image-text large">
                                    <FileProgress name={user.firstName + " " + user.lastName} imageUrl={user.profileImageUrl}
                                        style={"profile-size"} />
                                    <div onClick={() => { setSelectedUser(user); handleOpen("view"); }} className="user-data">
                                        <span className="large-text">{validStringField(user.firstName + " " + user.lastName)}</span>
                                        <span className="small-text">{validStringField(user.email)}</span>
                                    </div>
                                </div>
                                <div className="cell small">
                                    {user.status
                                        ? (<>
                                            <img className="dot" src={GreenDot} alt={"Green"} />
                                            <span className="large-text">Active</span>
                                        </>)
                                        : (<>
                                            <img className="dot" src={RedDot} alt={"Red"} />
                                            <span className="large-text">Inactive</span>
                                        </>)
                                    }
                                </div>
                                <div className="cell large justify">
                                    <span className="large-text">
                                        {user?.lastLogin
                                            ? (validDateField(user?.lastLogin))
                                            : ("-")
                                        }
                                    </span>
                                    <div>
                                        <img className="action" src={Edit} alt={"ArrowRight"}
                                            onClick={() => { setSelectedUser(user); handleOpen("edit"); }} />
                                    </div>
                                </div>
                            </div>)
                        })}
                    </div>
                </div >

                {userModals.add
                    ? (<AddUserModal open={userModals.add} close={handleClose} />)
                    : (<Fragment />)
                }
                {userModals.edit
                    ? (<EditUserModal open={userModals.edit} close={handleClose} selectedUserId={selectedUser.id} />)
                    : (<Fragment />)
                }
                {userModals.view
                    ? (<ViewUserModal open={userModals.view} close={handleClose} selectedUser={selectedUser} openEdit={handleOpen} />)
                    : (<Fragment />)
                }

                <SearchableDown onClick={(value: string) => { handleSelectedUser(value); }} onClose={handleSearcherClose}
                    anchorEl={anchorSearcherEl} open={openSearcher} initialDetails={getInitialValues()} searchable={showSearch} />
            </div >
        </Layout >
    );
}
